import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useParticipant } from "@/context/ParticipantContext";
import { CERTIFICATE } from "@/constants/testIds";
import { titleCase } from "@/lib/format";
import { api } from "@/lib/api";
import CertificateCard from "@/components/CertificateCard";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function Certificate() {
  const { participant, progress, refreshProgress } = useParticipant();
  const navigate = useNavigate();
  const certRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    refreshProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (progress && !progress.certified) {
      navigate("/training-manual");
    }
  }, [progress, navigate]);

  const dateStr = new Date().toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" });

  const handleDownload = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`DENSO_Certificate_${titleCase(participant?.name)}_${titleCase(participant?.surname)}.pdf`);
      if (participant?.id) {
        await api.post(`/participants/${participant.id}/mark-downloaded`);
      }
    } finally {
      setDownloading(false);
    }
  };

  if (!participant || !progress?.certified) {
    return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-500">Loading certificate...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <button
        onClick={() => navigate("/home")}
        data-testid={CERTIFICATE.backButton}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-denso-red transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="overflow-x-auto"
      >
        <CertificateCard
          ref={certRef}
          testId={CERTIFICATE.container}
          name={participant.name}
          surname={participant.surname}
          dateStr={dateStr}
          certificateId={progress.certificate_id}
        />
      </motion.div>

      <div className="flex justify-center">
        <Button
          data-testid={CERTIFICATE.downloadButton}
          onClick={handleDownload}
          disabled={downloading}
          className="bg-denso-red hover:bg-denso-lightred text-white h-11 px-6"
        >
          <Download className="h-4 w-4" /> {downloading ? "Generating..." : "Download Certificate (PDF)"}
        </Button>
      </div>
    </div>
  );
}
